import { DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { BlockUI, NgBlockUI } from 'ng-block-ui';
import { ToastrService } from 'ngx-toastr';
import { Observable, of, OperatorFunction, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ClientContact, ClientDataResponse } from 'src/app/models/client/client.interface';
import { ClientContactService } from 'src/app/services/client-contact.service';
import { ClientService } from 'src/app/services/client.service';
import { ProgramService } from 'src/app/services/program.service';

@Component({
  selector: 'app-client-contact-detail',
  templateUrl: './client-contact-detail.component.html',
  styleUrls: ['./client-contact-detail.component.css']
})
export class ClientContactDetailComponent implements OnInit, OnDestroy {
  activeTab = 'Overview';
  pageTabs = [
    'Overview',
    'Permission',
  ];
  generalInfo = false;
  noteInfo = false;
  quickLinkToggle = false;
  creditBalanceToggle = false;
  private unsubscribe$ = new Subject<void>();
  serverDateFormat = 'yyyy-MM-dd';
  quickLinks = [
    {
      name:'All Cases',
      link:'javascript:void(0)'
    },
    {
      name:'Open Cases',
      link:'javascript:void(0)'
    },
    {
      name:'Closed Cases',
      link:'javascript:void(0)'
    },
    {
      name:'All Invoices',
      link:'javascript:void(0)'
    },
    {
      name:'Unpaid Invoices',
      link:'javascript:void(0)'
    },
    {
      name:'Payment Histories',
      link:'javascript:void(0)'
    }
  ]

  creditBalances = [
    {
      name:"Outstanding",
      value:0
    },
    {
      name:"Past Due",
      value:0
    },
    {
      name:"Time Management",
      value:0
    }
  ]

  permissions = [
    'edit cases',
    'create cases',
    'view cases',
    'download',
    'view dashboard',
    'upload',
    'view files'
  ]
  clientContactData: ClientContact;
  @BlockUI('client-contact-details') clientContactsBlockUI: NgBlockUI;
  programContactForm: FormGroup;
  clientSearchLoader: boolean;
  clientSerchResult: any;
  programLocationSearchLoader: boolean;
  programLocationSerchResult: any;
  isClientContactFormSubmitted: boolean;
  @ViewChild('clientNameSearchInput') clientNameSearchInput: ElementRef;
  @ViewChild('programNameSearchInput') programNameSearchInput: ElementRef;
  @BlockUI('client-contact-program-details') clientContactProgramsBlockUI: NgBlockUI;
  clientContactProgramTotal: number;
  clientContactPrograms: any[];
  clientContactProgramPage = 1;
  clientContactProgramLimit: number = 20;

  constructor(
    private modalService: NgbModal,
    private route: ActivatedRoute,
    private clientService: ClientService,
    private fb: FormBuilder,
    private _datePipe: DatePipe,
    private toastr: ToastrService,
    private router: Router,
    private clientContactService:ClientContactService,
    private programService:ProgramService
  ) {

  }

  ngOnInit() {
    this.clientContactsBlockUI.start();
    this.route.params
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((params: Params) => this.fetchClientContactDetails(params.id))
      )
      .subscribe(()=>{
        this.clientContactsBlockUI.stop();
      });

    this.initProgramContactLocationForm();
    this.getClientContactPrograms();
  }

  fetchClientContactDetails(id: number) {
    return this.clientContactService.getClientContact(id).pipe(
      takeUntil(this.unsubscribe$),
      tap(({ details }: ClientDataResponse<ClientContact>) => {
        this.clientContactData = details;
      })
    );
  }

  public openPopup(content, size = 'md') {
    this.modalService.open(content, {
      ariaDescribedBy: 'modal-basic-title',
      windowClass: 'groupModal',
      size,
      centered: true,
    });
  }

  initProgramContactLocationForm() {
    this.programContactForm = this.fb.group({
      role: [''],
      program_location: [null, [Validators.required]],
      client_contact: [null, [Validators.required]],
    });
  }

  clientSearchFormatter = (client: any) => {
    this.programContactForm.get('client_contact').setValue(client.id);
    return client.full_name;
  };

  searchClient: OperatorFunction<string, readonly any[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      tap((term) => {
        if (term.length <= 2) {
          this.clientSearchLoader = false;
        }
      }),
      debounceTime(1000),
      distinctUntilChanged(),
      tap((term) => {
        if (term.length > 2) {
          this.clientSearchLoader = true;
        }
      }),
      switchMap((term) =>
        term.length < 3
          ? []
          : this.clientService.fetchClientContacts(20, 1, term).pipe(
              tap((result) => {
                if (result) {
                  this.clientSearchLoader = false;
                }
              }),
              map((results: any) => {
                this.clientSerchResult = results.details.hits;
                const startsWithResults = this.clientSerchResult.filter(
                  (result): any =>
                    result.full_name.toLowerCase().startsWith(term.toLowerCase())
                );
                const otherResults = this.clientSerchResult.filter(
                  (result) =>
                    !result.full_name.toLowerCase().startsWith(term.toLowerCase())
                );
                return [...startsWithResults, ...otherResults];
              }),
              catchError(() => {
                return of([]);
              })
            )
      )
    );

  programLocaionSearchFormatter = (item: any) => {
    this.programContactForm.get('program_location').setValue(item.id);
    return item.program_location_name;
  };

  searchProgramLocation: OperatorFunction<string, readonly any[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      tap((term) => {
        if (term.length <= 2) {
          this.programLocationSearchLoader = false;
        }
      }),
      debounceTime(1000),
      distinctUntilChanged(),
      tap((term) => {
        if (term.length > 2) {
          this.programLocationSearchLoader = true;
        }
      }),
      switchMap((term) =>
        term.length < 3
          ? []
          : this.programService.getProgramLocations(20, 1, term).pipe(
              tap((result) => {
                if (result) {
                  this.programLocationSearchLoader = false;
                }
              }),
              map((results: any) => {
                this.programLocationSerchResult = results.details.hits;
                const startsWithResults =
                  this.programLocationSerchResult.filter((result): any =>
                    result.program_location_name
                      .toLowerCase()
                      .startsWith(term.toLowerCase())
                  );
                const otherResults = this.programLocationSerchResult.filter(
                  (result) =>
                    !result.program_location_name
                      .toLowerCase()
                      .startsWith(term.toLowerCase())
                );
                return [...startsWithResults, ...otherResults];
              }),
              catchError(() => {
                return of([]);
              })
            )
      )
    );

  submitClientContactProgram() {
    if (this.programContactForm.invalid) {
      return;
    }


    this.isClientContactFormSubmitted = true;

    this.clientContactService
      .createClientContactProgram(this.programContactForm.value)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res) => {
          this.toastr.success(res?.details?.message);
          this.isClientContactFormSubmitted = false;
          this.getClientContactPrograms();
          this.clearInputs();
          this.programContactForm.reset();
          this.modalService.dismissAll();
        },
        (error) => {
          this.toastr.success(error?.errors[0]);
        }
      );
  }

  getClientContactPrograms() {
    this.clientContactProgramsBlockUI.start();
    this.clientContactService
      .getClientContactPrograms(this.clientContactProgramLimit, this.clientContactProgramPage)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (resp) => {
          const { hits, offset, estimatedTotalHits } = resp.details;
          this.clientContactPrograms = hits;
          this.clientContactProgramTotal = estimatedTotalHits;
          this.clientContactProgramsBlockUI.stop();
        },
        (error) => {
          this.clientContactProgramsBlockUI.stop();
        }
      );
  }

  onPageChangeClientContactPrograms(event: any) {
    this.clientContactProgramPage = event;
    this.getClientContactPrograms();
  }

  onSizeChangeClientContactPrograms(limit: number) {
    this.clientContactProgramLimit = limit;
    this.clientContactProgramPage = 1;
    this.getClientContactPrograms();
  }

  clearInputs(){
    if(this.clientNameSearchInput && this.clientNameSearchInput.nativeElement){
      this.clientNameSearchInput.nativeElement.value = '';
    }
    if(this.programNameSearchInput && this.programNameSearchInput.nativeElement){
      this.programNameSearchInput.nativeElement.value = '';
    }
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
